'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import SiteHeader from '@/components/SiteHeader';
import SiteFooter from '@/components/SiteFooter';
import {
  Utensils, Music, Dumbbell, ShoppingBag, Trophy, Globe,
  Plane, Building2, ArrowRight, AlertCircle, Loader2, Star, MapPin
} from 'lucide-react';

type PageState = 'loading' | 'populated' | 'unavailable';

interface NearbyItem {
  name: string;
  description: string;
  distance?: string;
  tag?: string;
}

interface NearbyCategory {
  id: string;
  icon: React.ElementType;
  title: string;
  accent: string;
  intro: string;
  items: NearbyItem[];
}

const nearbyCategories: NearbyCategory[] = [
  {
    id: 'dining',
    icon: Utensils,
    title: 'Dining',
    accent: '#FF3DF5',
    intro: 'World-class dining is minutes from your door. From Michelin-recognized restaurants to beloved local institutions.',
    items: [
      { name: 'Carbone Las Vegas', description: 'Italian-American fine dining at ARIA. One of the most sought-after reservations in the city.', distance: '~8 min', tag: 'Fine Dining' },
      { name: 'Bazaar Meat by José Andrés', description: 'A theatrical steakhouse experience at SAHARA. Exceptional quality and presentation.', distance: '~12 min', tag: 'Steakhouse' },
      { name: 'Estiatorio Milos', description: 'Greek seafood at The Cosmopolitan. Fresh, refined, and consistently excellent.', distance: '~10 min', tag: 'Seafood' },
      { name: 'Locale Italian Kitchen', description: 'A neighborhood Italian favorite near the Strip. Reliable, warm, and genuinely good.', distance: '~5 min', tag: 'Neighborhood' },
    ],
  },
  {
    id: 'nightlife',
    icon: Music,
    title: 'Nightlife',
    accent: '#7A5CFF',
    intro: 'Las Vegas nightlife is unmatched. Panorama puts you close enough to enjoy it — and far enough to sleep.',
    items: [
      { name: 'Zouk Nightclub', description: 'World-class DJ residencies at Resorts World. One of the premier nightlife destinations in the city.', distance: '~10 min', tag: 'Club' },
      { name: 'Omnia at Caesars', description: 'A landmark nightclub on the Strip. Multi-level, high-production, and consistently booked.', distance: '~8 min', tag: 'Club' },
      { name: 'Allegiant Stadium Events', description: "Major concerts and events at the Raiders' home stadium. A short drive from Panorama.", distance: '~10 min', tag: 'Venue' },
      { name: 'T-Mobile Arena', description: 'Home of the Golden Knights. One of the best sports and concert venues in the country.', distance: '~5 min', tag: 'Arena' },
    ],
  },
  {
    id: 'wellness',
    icon: Dumbbell,
    title: 'Wellness',
    accent: '#4CC9FF',
    intro: 'Las Vegas has a surprisingly strong wellness scene. Panorama residents know where to find it.',
    items: [
      { name: 'Qua Baths & Spa at Caesars', description: 'A full-service spa with Roman baths, arctic ice rooms, and premium treatments.', distance: '~8 min', tag: 'Spa' },
      { name: 'The Spa at Wynn', description: 'One of the most acclaimed hotel spas in Las Vegas. Serene, luxurious, and worth the trip.', distance: '~12 min', tag: 'Spa' },
      { name: 'Lifetime Fitness (Town Square)', description: 'A premium fitness club with pools, courts, and full amenity access.', distance: '~8 min', tag: 'Fitness' },
      { name: 'Red Rock Canyon', description: 'World-class hiking and climbing just 20 minutes from Panorama. A genuine natural escape.', distance: '~20 min', tag: 'Outdoor' },
    ],
  },
  {
    id: 'shopping',
    icon: ShoppingBag,
    title: 'Shopping',
    accent: '#FF3DF5',
    intro: "From luxury retail to everyday essentials — Panorama's location covers it all.",
    items: [
      { name: 'Crystals at CityCenter', description: 'The most curated luxury retail in Las Vegas. Louis Vuitton, Prada, Gucci, and more.', distance: '~8 min', tag: 'Luxury' },
      { name: 'The Forum Shops at Caesars', description: 'A landmark shopping destination with over 160 stores and restaurants.', distance: '~8 min', tag: 'Mall' },
      { name: 'Town Square Las Vegas', description: 'An open-air lifestyle center with retail, dining, and entertainment. A local favorite.', distance: '~8 min', tag: 'Lifestyle' },
      { name: 'Whole Foods Market', description: 'Premium grocery close to Panorama. Convenient for residents who prioritize quality.', distance: '~5 min', tag: 'Grocery' },
    ],
  },
  {
    id: 'sports',
    icon: Trophy,
    title: 'Sports & Entertainment',
    accent: '#7A5CFF',
    intro: 'Las Vegas is now a major sports city. Panorama residents are perfectly positioned for it.',
    items: [
      { name: 'Vegas Golden Knights', description: 'NHL champions at T-Mobile Arena. Game nights are a community event at Panorama.', distance: '~5 min', tag: 'NHL' },
      { name: 'Las Vegas Raiders', description: 'NFL football at Allegiant Stadium. One of the most impressive venues in the league.', distance: '~10 min', tag: 'NFL' },
      { name: 'Las Vegas Aces', description: 'WNBA champions at Michelob Ultra Arena. A world-class team in a great venue.', distance: '~5 min', tag: 'WNBA' },
      { name: 'Las Vegas Grand Prix Circuit', description: 'Formula 1 racing through the heart of the Strip. A spectacle unlike anything else.', distance: '~8 min', tag: 'F1' },
    ],
  },
  {
    id: 'sphere',
    icon: Globe,
    title: 'The Sphere & Major Venues',
    accent: '#4CC9FF',
    intro: 'Las Vegas has become home to some of the world\'s most extraordinary entertainment venues.',
    items: [
      { name: 'The Sphere', description: 'The most immersive entertainment experience on earth. Panorama residents plan ahead for major shows.', distance: '~12 min', tag: 'Landmark' },
      { name: 'Dolby Live at Park MGM', description: 'A premium concert venue with exceptional acoustics and intimate scale.', distance: '~5 min', tag: 'Concert' },
      { name: 'Resorts World Theatre', description: 'A state-of-the-art venue hosting major residencies and touring acts.', distance: '~10 min', tag: 'Concert' },
      { name: 'Las Vegas Convention Center', description: 'The hub for CES, NAB, SEMA, and dozens of major conventions annually.', distance: '~8 min', tag: 'Convention' },
    ],
  },
  {
    id: 'airport',
    icon: Plane,
    title: 'Airport Convenience',
    accent: '#FF3DF5',
    intro: 'Harry Reid International Airport is one of the most convenient airports in the country. Panorama makes it even easier.',
    items: [
      { name: 'Harry Reid International Airport', description: 'Approximately 10 minutes from Panorama. No highway required — a straight shot down Dean Martin.', distance: '~10 min', tag: 'Airport' },
      { name: 'No Traffic Complexity', description: 'The route from Panorama to the airport avoids Strip congestion entirely. Residents consistently note this as a major advantage.', tag: 'Advantage' },
      { name: 'Valet-to-Terminal', description: 'Valet drop-off at Panorama, direct to the terminal. The lock-and-leave lifestyle starts at the front door.', tag: 'Convenience' },
    ],
  },
  {
    id: 'convention',
    icon: Building2,
    title: 'Convention Convenience',
    accent: '#7A5CFF',
    intro: 'Las Vegas hosts more major conventions than any city in the world. Panorama is the ideal base.',
    items: [
      { name: 'CES (Consumer Electronics Show)', description: 'The world\'s largest tech convention. January. Panorama is a natural base for attendees and exhibitors.', tag: 'Annual' },
      { name: 'NAB Show', description: 'The broadcast and media industry\'s flagship event. April. Minutes from Panorama.', tag: 'Annual' },
      { name: 'SEMA Show', description: 'The automotive industry\'s premier event. November. Allegiant Stadium and LVCC are both close.', tag: 'Annual' },
      { name: 'Magic & Coterie', description: 'Fashion industry trade shows. Multiple times per year. Panorama is a preferred address for attendees.', tag: 'Annual' },
    ],
  },
];

export default function NearbyPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [activeCategory, setActiveCategory] = useState<string>('all');

  useEffect(() => {
    const t = setTimeout(() => setPageState('populated'), 850);
    return () => clearTimeout(t);
  }, []);

  const visibleCategories = activeCategory === 'all'
    ? nearbyCategories
    : nearbyCategories.filter((c) => c.id === activeCategory);

  return (
    <div className="min-h-screen" style={{ background: '#07060C' }}>
      <SiteHeader theme="transparent" />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 overflow-hidden panorama-hero-bg">
          <div
            className="absolute top-0 right-0 w-[55%] h-[75%] pointer-events-none"
            style={{ background: 'radial-gradient(ellipse 70% 65% at 100% 5%, rgba(76,201,255,0.18) 0%, rgba(122,92,255,0.08) 50%, transparent 70%)' }}
          />
          <div className="relative z-10 max-w-screen-2xl mx-auto px-6 lg:px-10">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
              style={{ borderRadius: '6px', background: 'rgba(76,201,255,0.10)', border: '1px solid rgba(76,201,255,0.22)' }}
            >
              <MapPin size={12} style={{ color: '#4CC9FF' }} />
              <span className="text-[12px] font-medium tracking-wide" style={{ color: '#4CC9FF' }}>
                Las Vegas Living
              </span>
            </div>
            <h1
              className="font-display font-extrabold text-[48px] sm:text-[60px] lg:text-[68px] leading-[1.04] tracking-tight mb-5 max-w-3xl"
              style={{ color: '#F2F4FF' }}
            >
              Everything Las Vegas.{' '}
              <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #4CC9FF 0%, #7A5CFF 100%)' }}>
                Minutes away.
              </span>
            </h1>
            <p className="text-[18px] leading-relaxed max-w-2xl mb-10 font-light" style={{ color: 'rgba(242,244,255,0.72)' }}>
              Panorama's location on Dean Martin Drive puts you within reach of the Strip, the airport, world-class dining, sports, entertainment, and the best of Las Vegas — without living in the middle of it.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/request-access" className="btn-primary group">
                Request Access
                <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link href="/lifestyle" className="btn-secondary">
                See the Lifestyle
              </Link>
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 neon-divider" />
        </section>

        {/* Loading */}
        {pageState === 'loading' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 size={32} className="animate-spin" style={{ color: '#4CC9FF' }} />
              <p className="text-[15px]" style={{ color: '#A7A3C4' }}>Loading…</p>
            </div>
          </section>
        )}

        {/* Unavailable */}
        {pageState === 'unavailable' && (
          <section className="py-24" style={{ background: '#0C0A16' }}>
            <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 flex flex-col items-center justify-center py-20 gap-4 text-center">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,61,245,0.10)', border: '1px solid rgba(255,61,245,0.20)' }}>
                <AlertCircle size={22} style={{ color: '#FF3DF5' }} />
              </div>
              <h3 className="font-display font-semibold text-[20px]" style={{ color: '#F2F4FF' }}>Content unavailable</h3>
              <p className="text-[14px] max-w-sm" style={{ color: '#A7A3C4' }}>We're having trouble loading this page. Please try again shortly.</p>
              <Link href="/" className="btn-secondary text-[13px] px-4 py-2 mt-2">Return Home</Link>
            </div>
          </section>
        )}

        {/* Populated */}
        {pageState === 'populated' && (
          <>
            {/* Category filter */}
            <section style={{ background: '#0C0A16', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 py-4 overflow-x-auto">
                <div className="flex gap-2 min-w-max">
                  <button
                    onClick={() => setActiveCategory('all')}
                    className="px-3.5 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150"
                    style={activeCategory === 'all'
                      ? { background: 'rgba(76,201,255,0.12)', color: '#4CC9FF', border: '1px solid rgba(76,201,255,0.25)' }
                      : { background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    All
                  </button>
                  {nearbyCategories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setActiveCategory(cat.id)}
                      className="px-3.5 py-1.5 rounded-lg text-[12px] font-medium transition-all duration-150"
                      style={activeCategory === cat.id
                        ? { background: `${cat.accent}12`, color: cat.accent, border: `1px solid ${cat.accent}25` }
                        : { background: 'rgba(255,255,255,0.04)', color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.07)' }}
                    >
                      {cat.title}
                    </button>
                  ))}
                </div>
              </div>
            </section>

            {/* Categories */}
            <section className="py-16" style={{ background: '#0C0A16' }}>
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 space-y-16">
                {visibleCategories.map((cat, idx) => (
                  <div key={cat.id}>
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center"
                        style={{ background: `${cat.accent}12`, border: `1px solid ${cat.accent}22` }}
                      >
                        <cat.icon size={18} style={{ color: cat.accent }} />
                      </div>
                      <h2 className="font-display font-bold text-[24px]" style={{ color: '#F2F4FF' }}>
                        {cat.title}
                      </h2>
                    </div>
                    <p className="text-[14px] leading-relaxed mb-6 max-w-2xl" style={{ color: '#A7A3C4' }}>
                      {cat.intro}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {cat.items.map((item) => (
                        <div key={item.name} className="neon-card p-5">
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-display font-semibold text-[14px] leading-snug pr-2" style={{ color: '#F2F4FF' }}>
                              {item.name}
                            </h3>
                            {item.tag && (
                              <span
                                className="text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0"
                                style={{ background: `${cat.accent}10`, color: cat.accent, border: `1px solid ${cat.accent}20` }}
                              >
                                {item.tag}
                              </span>
                            )}
                          </div>
                          <p className="text-[12.5px] leading-relaxed mb-3" style={{ color: '#A7A3C4' }}>
                            {item.description}
                          </p>
                          {item.distance && (
                            <div className="flex items-center gap-1.5">
                              <MapPin size={11} style={{ color: 'rgba(167,163,196,0.50)' }} />
                              <span className="text-[11px]" style={{ color: 'rgba(167,163,196,0.50)' }}>{item.distance} from Panorama</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {idx < visibleCategories.length - 1 && <div className="neon-divider mt-16" />}
                  </div>
                ))}
              </div>
            </section>

            {/* Home base section */}
            <section className="py-20" style={{ background: '#07060C' }}>
              <div className="neon-divider mb-0" />
              <div className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-20">
                <div
                  className="relative overflow-hidden panorama-premium-bg rounded-xl p-10 lg:p-14"
                  style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="absolute top-0 right-0 w-[50%] h-full pointer-events-none" style={{ background: 'radial-gradient(ellipse 80% 70% at 100% 20%, rgba(76,201,255,0.12) 0%, transparent 65%)' }} />
                  <div className="relative z-10 max-w-2xl">
                    <div
                      className="inline-flex items-center gap-2 px-3 py-1.5 mb-6"
                      style={{ borderRadius: '6px', background: 'rgba(76,201,255,0.10)', border: '1px solid rgba(76,201,255,0.22)' }}
                    >
                      <Star size={12} style={{ color: '#4CC9FF' }} />
                      <span className="text-[12px] font-medium tracking-wide" style={{ color: '#4CC9FF' }}>
                        Why Panorama Works as a Home Base
                      </span>
                    </div>
                    <h2 className="font-display font-bold text-[32px] sm:text-[38px] leading-tight tracking-tight mb-5" style={{ color: '#F2F4FF' }}>
                      The best address in Las Vegas for how you actually live.
                    </h2>
                    <p className="text-[16px] leading-relaxed mb-4" style={{ color: 'rgba(167,163,196,0.90)' }}>
                      Whether you're here full-time, part-time, or using Panorama as a base for business and entertainment, the location delivers. Airport in 10 minutes. Strip in 5. Convention center in 8. And a building that feels like a reward when you return.
                    </p>
                    <p className="text-[15px] leading-relaxed mb-8" style={{ color: 'rgba(167,163,196,0.75)' }}>
                      Panorama residents consistently say the location is one of the top reasons they chose this building — and one of the top reasons they stay.
                    </p>
                    <div className="flex flex-wrap gap-4">
                      <Link href="/request-access" className="btn-primary group inline-flex">
                        Request Access
                        <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5" />
                      </Link>
                      <Link href="/why-panorama" className="btn-secondary">
                        Why Panorama
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Cross-links */}
                <div
                  className="mt-14 pt-10 border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.06)' }}
                >
                  <p className="text-[11px] font-semibold tracking-[0.10em] uppercase mb-5 text-center" style={{ color: 'rgba(167,163,196,0.35)' }}>
                    Explore more
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Link href="/amenities" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Amenities
                    </Link>
                    <Link href="/lifestyle" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Lifestyle
                    </Link>
                    <Link href="/community" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      The Community
                    </Link>
                    <Link href="/real-estate" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      Real Estate
                    </Link>
                    <Link href="/faq" className="text-[13px] font-medium px-4 py-2 rounded-lg transition-all duration-150 hover:bg-white/[0.06]" style={{ color: '#A7A3C4', border: '1px solid rgba(255,255,255,0.08)' }}>
                      FAQ
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
